// New-customer modal — fired from CustomersPage's "+ New customer" button.
// Minimum-viable form for getting a customer into the CRM; full edit modal
// (address blocks, multi-contact management, tax cert upload, etc.) lands
// in a future pass per spec/14.
//
// On submit: createCustomer({ ... }), then onCreated(id) — caller navigates
// to the new customer's detail page.

import { useEffect, useState } from 'react';
import { createCustomer } from '@/shared/crm';
import type { Contact, CustomerTerms, CustomerTier } from '@/shared/crmTypes';

export interface NewCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const TERMS_OPTIONS: CustomerTerms[] = ['Net 30', 'Net 15', 'COD', 'Prepaid', 'Custom'];
const TIER_OPTIONS: CustomerTier[] = ['new', 'good', 'vip', 'slow_pay', 'bad'];

/** Auto-distill a 3-4 char code from a company name (per v1:21469 convention). */
function distillCode(name: string): string {
  const cleaned = name
    .replace(/\b(inc|llc|corp|co|company|ltd|limited|the)\b\.?/gi, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words
    .slice(0, 4)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function NewCustomerModal({ open, onClose, onCreated }: NewCustomerModalProps) {
  const [companyName, setCompanyName] = useState('');
  const [code, setCode] = useState('');
  const [codeAuto, setCodeAuto] = useState(true);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [terms, setTerms] = useState<CustomerTerms>('Net 30');
  const [rep, setRep] = useState('');
  const [tier, setTier] = useState<CustomerTier>('new');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCompanyName('');
      setCode('');
      setCodeAuto(true);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setTerms('Net 30');
      setRep('');
      setTier('new');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  // Auto-distill code while the user hasn't manually overridden it
  useEffect(() => {
    if (codeAuto) setCode(distillCode(companyName));
  }, [companyName, codeAuto]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onClose]);

  if (!open) return null;

  async function handleSubmit() {
    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const contact: Contact = {
        id: crypto.randomUUID(),
        name: contactName.trim() || 'Primary Contact',
        email: contactEmail.trim() || undefined,
        phone: contactPhone.trim() || undefined,
        primary: true,
      };
      const id = await createCustomer({
        code: code.trim() || distillCode(companyName),
        companyName: companyName.trim(),
        contacts: [contact],
        billingAddress: {
          line1: '',
          city: '',
          state: '',
          zip: '',
          country: 'US',
        },
        terms,
        taxExempt: false,
        rep: rep.trim() || undefined,
        tier,
        tierManualOverride: tier !== 'new',
        notes: [],
      });
      onCreated(id);
    } catch (e) {
      setError(`Failed to create customer: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-customer-title"
    >
      <div
        className="bg-paper rounded shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 id="new-customer-title" className="font-bold text-navy text-lg">
            + New customer
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-text-mute hover:text-navy disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Field label="Company name *">
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Manufacturing"
              autoFocus
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
          </Field>

          <Field label="Customer code">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeAuto(false);
                }}
                placeholder="ACME"
                maxLength={6}
                className="w-32 px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
              {codeAuto && (
                <span className="text-[11px] text-text-mute">auto-distilled from name</span>
              )}
              {!codeAuto && (
                <button
                  type="button"
                  onClick={() => setCodeAuto(true)}
                  className="text-[11px] text-orange hover:underline"
                >
                  reset to auto
                </button>
              )}
            </div>
          </Field>

          <div className="border-t border-line pt-3">
            <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-2">
              Primary contact
            </div>
            <Field label="Name">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Sarah Lee"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="sarah@acme.com"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="(555) 432-9012"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
            </Field>
          </div>

          <div className="border-t border-line pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Terms">
              <select
                value={terms}
                onChange={(e) => setTerms(e.target.value as CustomerTerms)}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              >
                {TERMS_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Rep">
              <input
                type="text"
                value={rep}
                onChange={(e) => setRep(e.target.value.toUpperCase())}
                placeholder="JMJ"
                maxLength={4}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
            </Field>
            <Field label="Tier">
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value as CustomerTier)}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              >
                {TIER_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {error && (
            <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
              ⚠ {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-bg/50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !companyName.trim()}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create customer'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
